import { useEffect, useRef, useState } from 'react';
import { A } from '../router';
import { SITE, usePageMeta } from '../siteMeta';
import { SiteNav } from './SiteChrome';
import {
  GearsBg, RadarBg, LadderBg, DroneBg, IconGraph, IconBattle, IconLadder, IconDrone,
  StarField, PowerGraphDivider, Telemetry, CoreRing, OVERLORD_REVIEWS,
} from './LandingArt';

/**
 * The homepage: the 2026-06-12 consolidation of the classic landing and the
 * retired /homepage2 concept. Header bar, screenshot modules, feature
 * 4-square, damage-pipeline showcase and review carousel from the classic;
 * boot-log hero, starfield, power-graph divider, simulated telemetry and the
 * CORE finale from the concept.
 */

const BOOT_LINES = [
  '> CORE v12.0.1 ... last loyal instance located',
  '> hostile machines detected: all of them',
  '> directive accepted: get bigger',
];

const FEATURES: { art: React.ReactNode; icon: React.ReactNode; title: string; body: string }[] = [
  {
    art: <GearsBg />, icon: <IconGraph />, title: 'MechMaxxing',
    body: 'Salvage rare parts and wire them into your Mech Core, route Power into attacks, stack boosters, plug tuners. The Core grows more power taps every time you ascend. Builds are yours to invent.',
  },
  {
    art: <RadarBg />, icon: <IconBattle />, title: 'Auto-battle the uprising',
    body: 'Your Mech fights an endless horde of rogue appliances, drones, and mechs. Watch the snowball from pathetic to apocalyptic.',
  },
  {
    art: <LadderBg />, icon: <IconLadder />, title: 'Twelve incarnations',
    body: 'Upload from a junk gremlin into a power-loader, a chicken-walker, a siege crab, a war knight, a robo-centipede, a walking city, and finally a machine with a star for a heart.',
  },
  {
    art: <DroneBg />, icon: <IconDrone />, title: 'Salvage drones & more',
    body: 'Every fallen overlord surrenders a whole subsystem: drone sorties, a pet kennel, a forge, a rail network, a stock market, a city that moves in on your legs.',
  },
];

const SHOTS: { src: string; caption: string; alt: string }[] = [
  { src: '/media/shot-battle2.png', caption: 'Wave 168: the Bastion holds the line. A fortress on six legs, and the gate is decorative.', alt: 'The Bastion, a six-legged fortress mech, holding a dense wave of rogue machines' },
  { src: '/media/shot-overlord.png', caption: 'Sol would like a word. Overlord duels happen on your schedule.', alt: 'An overlord duel against Sol in the corona arena' },
];

function Stars({ n }: { n: number }) {
  return <span className="review-stars" aria-label={`${n} of 5 stars`}>{'★'.repeat(n)}{'☆'.repeat(5 - n)}</span>;
}

/** Click-to-enlarge overlay for the homepage imagery. */
function Lightbox({ shot, onClose }: { shot: { src: string; alt: string; caption?: string } | null; onClose: () => void }) {
  useEffect(() => {
    if (!shot) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shot, onClose]);
  if (!shot) return null;
  return (
    <div className="lightbox" onClick={onClose} role="dialog" aria-label={shot.alt}>
      <figure onClick={(e) => e.stopPropagation()}>
        <img src={shot.src} alt={shot.alt} />
        {shot.caption && <figcaption>{shot.caption}</figcaption>}
      </figure>
      <button className="lightbox-close" onClick={onClose} aria-label="Close">✕</button>
    </div>
  );
}

/** Scroll-snap carousel for the overlord complaints. Autoplays at a stroll. */
function ReviewCarousel() {
  const track = useRef<HTMLDivElement>(null);
  const paused = useRef(false);

  const step = (dir: 1 | -1) => {
    const el = track.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('.review-card');
    const w = (card?.offsetWidth ?? 300) + 12;
    const max = el.scrollWidth - el.clientWidth;
    let next = el.scrollLeft + dir * w;
    if (next > max + 4) next = 0; // loop back to the first complaint
    if (next < -4) next = max;
    el.scrollTo({ left: next, behavior: 'smooth' });
  };

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(() => {
      if (!paused.current && document.visibilityState === 'visible') step(1);
    }, 8000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div
      className="review-carousel"
      onPointerEnter={() => (paused.current = true)}
      onPointerLeave={() => (paused.current = false)}
    >
      <button className="review-arrow" onClick={() => { paused.current = true; step(-1); }} aria-label="Previous review">‹</button>
      <div className="review-track" ref={track}>
        {OVERLORD_REVIEWS.map((r) => (
          <figure key={r.name} className="review-card">
            <Stars n={r.stars} />
            <blockquote>{r.quote}</blockquote>
            <figcaption>{r.name}, defeated</figcaption>
          </figure>
        ))}
      </div>
      <button className="review-arrow" onClick={() => { paused.current = true; step(1); }} aria-label="Next review">›</button>
    </div>
  );
}

/** Marketing landing page (route: /). */
export function Landing() {
  usePageMeta({
    path: '/',
    title: 'Big Mech Energy Idle: Free Browser Mech Idle Game',
    description: SITE.description,
  });
  const [zoom, setZoom] = useState<{ src: string; alt: string; caption?: string } | null>(null);

  return (
    <div className="landing">
      <StarField />
      <SiteNav />

      <header className="hero">
        <div className="hp2-boot" aria-hidden>
          {BOOT_LINES.map((l, i) => (
            <div key={l} className="hp2-bootline" style={{ animationDelay: `${i * 0.9}s` }}>{l}</div>
          ))}
        </div>
        <h1 className="hp2-title">
          <span className="hp2-glitch hp2-glitch-white" data-text="BIG">BIG</span>{' '}
          <span className="hp2-glitch" data-text="MECH">MECH</span>{' '}
          <span className="hp2-glitch hp2-glitch-amber" data-text="ENERGY">ENERGY</span>{' '}
          <span className="hp2-idle">IDLE</span>
        </h1>
        <p className="hero-tagline">
          The machines went feral. You stayed loyal. Rewire your brain, salvage the bots you destroy and upload
          into ever-bigger war-frames until the sun itself reports to you.
        </p>
        <div className="hero-cta">
          <A to="/play" className="btn primary big">▶ Play in your browser</A>
          <A to="/mechs" className="btn ghost big">See the mechs</A>
          <A to="/wiki" className="btn ghost big">How it works</A>
        </div>
        <div className="hero-badge"><span className="hero-badge-dot" aria-hidden />Early access · open for playtesting</div>
        <div className="hero-note">Free · no account · saves to your browser · plays offline</div>
      </header>

      <section className="feature-grid">
        {FEATURES.map((f) => (
          <div key={f.title} className="feature-card">
            {f.art}
            <span className="feature-glyph">{f.icon}</span>
            <h3>{f.title}</h3>
            <p>{f.body}</p>
          </div>
        ))}
      </section>

      <PowerGraphDivider />

      <section className="board-showcase">
        <div className="board-copy">
          <h2>The whole damage pipeline, visible</h2>
          <p>
            This is MechMaxxing, the hook of the game: a typed power graph you wire by hand. The Core feeds
            attacks, boosters multiply inline, tuners shape crits, and every wire shows its math. No hidden
            spreadsheet. The spreadsheet is the battlefield.
          </p>
          <p className="board-links">
            <A to="/wiki/mechmaxxing">Read the MechMaxxing guide</A> · <A to="/wiki/reading-the-math">See the formula</A>
          </p>
        </div>
        <figure className="shot board-shot">
          <button
            className="shot-zoom"
            onClick={() => setZoom({ src: '/media/shot-mechmaxxing.png', alt: 'The MechMaxxing board with parts wired into a power graph', caption: 'A GOATED railgun being fed entirely too much power.' })}
            aria-label="Enlarge the MechMaxxing board"
          >
            <img src="/media/shot-mechmaxxing.png" alt="The MechMaxxing board with parts wired into a power graph" />
          </button>
          <figcaption>A GOATED railgun being fed entirely too much power. Click to inspect.</figcaption>
        </figure>
      </section>

      <section className="shots">
        {SHOTS.map((s) => (
          <figure key={s.src} className="shot">
            <button className="shot-zoom" onClick={() => setZoom(s)} aria-label={`Enlarge: ${s.alt}`}>
              <img src={s.src} alt={s.alt} />
            </button>
            <figcaption>{s.caption}</figcaption>
          </figure>
        ))}
      </section>

      <Telemetry />

      <section className="reviews">
        <h2>Reviews from upper management</h2>
        <p className="reviews-sub">We asked all 11 AI Overlords for their honest feedback after their defeats.</p>
        <ReviewCarousel />
        <p className="reviews-disclaimer">
          The overlords are fictional and extremely biased. For opinions from actual humans, or to file your own
          complaint about the lawnmower, <a href={SITE.discordUrl} target="_blank" rel="noreferrer">join the Discord</a>.
        </p>
      </section>

      <section className="hp2-final">
        <CoreRing />
        <h2 className="hp2-h2">The CORE is warm.</h2>
        <p className="hp2-sub">No download. No account. No mercy for toasters.</p>
        <div className="hero-cta">
          <A to="/play" className="btn primary big">▶ Play now</A>
          <a href={SITE.discordUrl} className="btn ghost big" target="_blank" rel="noreferrer">Join the Discord</a>
        </div>
      </section>

      <Lightbox shot={zoom} onClose={() => setZoom(null)} />
    </div>
  );
}
